import { Fragment, useState } from 'react';
import { Check, ChevronDown, ChevronRight, Download, FileSpreadsheet, Calculator, X } from 'lucide-react';
import Button from './Button.js';
import DocumentModal from './DocumentModal.js';
import ReceiptExtractedFields from './ReceiptExtractedFields.js';
import SettlementAuditFields from './SettlementAuditFields.js';
import SettlementFieldList from './SettlementFieldList.js';
import SettlementModalShell from './SettlementModalShell.js';
import SettlementReceiptThumb from './SettlementReceiptThumb.js';
import { fullCurrency } from '../../utils/formatCurrency.js';
import { formatIndianDate, formatIstDateTime } from '../../utils/localDate.js';
import { downloadBlob } from '../../utils/downloadBlob.js';
import { statusBadge, statusLabel } from '../paid-amount-settlement/paidAmountSettlementUi.js';

const formatIsoTimestamps = (text) =>
  String(text || '').replace(
    /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:[zZ]|[+-]\d{2}:?\d{2})?/g,
    (iso) => {
      const formatted = formatIstDateTime(iso);
      return formatted === '—' ? iso : formatted;
    },
  );

const friendlyNotes = (text) =>
  formatIsoTimestamps(
    String(text || '')
      .replace(/receipt OCR/gi, 'AI review')
      .replace(/\bOCR\b/g, 'AI'),
  );

const aiReviewLabel = (ocrStatus, requestStatus) => {
  if (requestStatus === 'OCR_PENDING') return 'AI verifying';
  const map = {
    PENDING: 'Queued',
    PROCESSING: 'AI verifying',
    PASSED: 'AI passed',
    FAILED: 'AI failed',
    ERROR: 'AI error',
  };
  return map[ocrStatus] || ocrStatus || '—';
};

const gcLabel = (ids) => {
  const list = ids || [];
  if (!list.length) return '—';
  if (list.length <= 2) return list.join(', ');
  return `${list.slice(0, 2).join(', ')} +${list.length - 2}`;
};

const LineTable = ({ title, columns, rows }) => (
  <div className="hidden md:block bg-white rounded-xl border border-gray-100 overflow-hidden">
    <div className="px-3 py-2 border-b border-gray-100">
      <h3 className="text-xs font-semibold text-gray-800">{title}</h3>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full text-xs min-w-[640px]">
        <thead className="bg-gray-50 text-gray-600">
          <tr>
            {columns.map((col) => (
              <th key={col.key} className={`px-2.5 py-2 font-semibold whitespace-nowrap ${col.align === 'right' ? 'text-right' : 'text-left'}`}>
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row) => (
            <tr key={row.key}>
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={`px-2.5 py-2 ${col.align === 'right' ? 'text-right tabular-nums whitespace-nowrap' : 'whitespace-nowrap'} ${col.className || ''}`}
                >
                  {col.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const PaidLineCards = ({ rows }) => (
  <div className="md:hidden space-y-2">
    <h3 className="text-xs font-semibold text-gray-800">Paid remittance</h3>
    {rows.map((row) => (
      <div key={row.key} className="rounded-xl border border-gray-100 bg-white p-3">
        <div className="flex items-start justify-between gap-2">
          <p className="font-semibold text-sm break-all min-w-0">{row.ref}</p>
          <p className="tabular-nums font-semibold text-sm shrink-0">{fullCurrency(row.amount)}</p>
        </div>
        <p className="text-[11px] text-gray-500 mt-0.5">{formatIndianDate(row.booking_date)} · {row.mode_of_payment || '—'}</p>
        <div className="grid grid-cols-2 gap-x-3 gap-y-1 mt-2 text-[11px] text-gray-600">
          <span>Paid <span className="tabular-nums font-medium text-gray-900">{fullCurrency(row.paid_amount)}</span></span>
          <span>Already remitted <span className="tabular-nums font-medium text-gray-900">{fullCurrency(row.previously_remitted)}</span></span>
          <span>Remaining <span className="tabular-nums font-medium text-gray-900">{fullCurrency(row.remaining_after)}</span></span>
          <span className="capitalize">{row.paid_remit_status || '—'}</span>
        </div>
      </div>
    ))}
  </div>
);

const roundMoney = (value) => Math.round((Number(value) || 0) * 100) / 100;

/** Prefer net kept (after TDS); fall back to gross / freight × %. */
const commissionOnFreight = (row) => {
  if (row?.net_amount != null && Number.isFinite(Number(row.net_amount))) {
    return roundMoney(row.net_amount);
  }
  const freight = Number(row?.total_freight || 0);
  const pct = Number(row?.commission_pct || 0);
  if (freight > 0 && pct > 0) return roundMoney((freight * pct) / 100);
  return roundMoney(row?.commission_amount);
};

const commissionLabel = (row) => {
  const pct = row?.commission_pct;
  const amount = commissionOnFreight(row);
  if (pct != null && pct !== '' && Number(pct) > 0) {
    return `${pct}% · ${fullCurrency(amount)}`;
  }
  return fullCurrency(amount);
};

const AmountCard = ({ label, value, tone }) => {
  const tones = {
    amber: 'border-amber-100 bg-amber-50/60',
    sky: 'border-sky-100 bg-sky-50/60',
    violet: 'border-violet-100 bg-violet-50/60',
    emerald: 'border-emerald-100 bg-emerald-50/60',
  };
  return (
    <div className={`rounded-xl border p-2.5 min-w-0 ${tones[tone] || 'border-gray-100 bg-gray-50'}`}>
      <p className="text-[11px] text-gray-500">{label}</p>
      <p className="font-semibold tabular-nums text-sm sm:text-base">{fullCurrency(value)}</p>
    </div>
  );
};

const modeLabel = (mode) => {
  const raw = String(mode || '').toLowerCase();
  if (raw === 'to_pay') return 'To-Pay';
  if (raw === 'paid') return 'Paid';
  if (raw === 'credit') return 'Credit';
  return mode || '—';
};

const CommissionCalcSummaryCard = ({ breakdown, onOpen }) => {
  if (!breakdown?.sheets?.length) return null;
  const sheetCount = breakdown.sheets.length;
  const gcCount = breakdown.sheets.reduce((n, s) => n + (s.gcs || []).length, 0);
  const pct = breakdown.commission_pct;
  return (
    <div className="rounded-xl border border-violet-100 bg-violet-50/50 px-3 py-2.5 flex flex-wrap items-center justify-between gap-2">
      <div className="min-w-0">
        <p className="text-xs font-semibold text-violet-950">
          Commission calculation
          {pct != null ? ` (${pct}%)` : ''}
        </p>
        <p className="text-[11px] text-violet-900/80 mt-0.5 leading-snug">
          {sheetCount} ML sheet{sheetCount === 1 ? '' : 's'} · {gcCount} GC{gcCount === 1 ? '' : 's'} · kept{' '}
          <span className="tabular-nums font-medium text-violet-950">
            {fullCurrency(breakdown.commission_net)}
          </span>
        </p>
      </div>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        icon={Calculator}
        onClick={onOpen}
        className="!px-2.5 !py-1.5 text-xs shrink-0"
      >
        View calculation
      </Button>
    </div>
  );
};

const CommissionBreakdownModal = ({ breakdown, batchId, onClose }) => {
  if (!breakdown?.sheets?.length) return null;
  const pct = breakdown.commission_pct;
  const tdsPct = breakdown.tds_pct;
  const formula = breakdown.remittance_formula || {};
  return (
    <SettlementModalShell
      title="Commission calculation"
      subtitle={batchId ? `${batchId}${pct != null ? ` · ${pct}% on delivery freight` : ''}` : undefined}
      onClose={onClose}
      size="xl"
      zIndex={95}
    >
      <div className="flex flex-col gap-3">
        <p className="text-[11px] text-gray-600 leading-snug">
          Includes Paid, Credit and To-Pay freight for this agent — not only the To-Pay remittance GC.
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <AmountCard label="Freight base" value={breakdown.freight_base} tone="sky" />
          <AmountCard label="Gross commission" value={breakdown.commission_gross} tone="violet" />
          <AmountCard
            label={tdsPct ? `TDS (${tdsPct}%)` : 'TDS'}
            value={breakdown.tds_amount}
            tone="amber"
          />
          <AmountCard label="Commission kept" value={breakdown.commission_net} tone="emerald" />
        </div>

        <div className="space-y-3">
          {breakdown.sheets.map((sheet) => {
            const gcs = sheet.gcs || [];
            return (
              <div
                key={sheet.ml_sheet_id}
                className="rounded-xl border border-gray-100 bg-white overflow-hidden"
              >
                <div className="px-3 py-2 border-b border-gray-100 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 bg-gray-50/80">
                  <p className="text-xs font-semibold text-gray-900 break-all">{sheet.ml_sheet_id}</p>
                  <p className="text-[11px] text-gray-600 tabular-nums">
                    {gcs.length} GC{gcs.length === 1 ? '' : 's'} · Gross {fullCurrency(sheet.commission_gross)}
                    {Number(sheet.tds_amount) > 0 ? (
                      <> · TDS {fullCurrency(sheet.tds_amount)} · Net {fullCurrency(sheet.commission_net)}</>
                    ) : null}
                  </p>
                </div>
                <div className="overflow-auto max-h-[min(40vh,22rem)]">
                  <table className="w-full text-[11px] min-w-[420px]">
                    <thead className="bg-white sticky top-0 z-[1] text-gray-500 shadow-[0_1px_0_0_rgba(0,0,0,0.06)]">
                      <tr>
                        <th className="text-left font-semibold px-3 py-1.5">GC</th>
                        <th className="text-left font-semibold px-2 py-1.5">Type</th>
                        <th className="text-right font-semibold px-2 py-1.5">Freight</th>
                        <th className="text-right font-semibold px-3 py-1.5">
                          {pct != null ? `${pct}%` : 'Comm.'}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {gcs.map((gc) => (
                        <tr key={gc.shipment_id}>
                          <td className="px-3 py-1.5 font-medium text-gray-800 whitespace-nowrap">
                            {gc.shipment_id}
                          </td>
                          <td className="px-2 py-1.5 text-gray-600 whitespace-nowrap">
                            {modeLabel(gc.mode_of_payment)}
                          </td>
                          <td className="px-2 py-1.5 text-right tabular-nums text-gray-800">
                            {fullCurrency(gc.freight)}
                          </td>
                          <td className="px-3 py-1.5 text-right tabular-nums text-gray-900 font-medium">
                            {fullCurrency(gc.commission)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="sticky bottom-0 bg-violet-50/90 border-t border-violet-100">
                      <tr>
                        <td className="px-3 py-1.5 font-semibold text-gray-800" colSpan={2}>
                          Sheet total
                        </td>
                        <td className="px-2 py-1.5 text-right tabular-nums font-semibold text-gray-800">
                          {fullCurrency(sheet.freight_base)}
                        </td>
                        <td className="px-3 py-1.5 text-right tabular-nums font-semibold text-gray-900">
                          {fullCurrency(sheet.commission_gross)}
                          {Number(sheet.tds_amount) > 0 ? (
                            <span className="block text-[10px] font-normal text-gray-500">
                              after {tdsPct || sheet.tds_pct}% TDS → {fullCurrency(sheet.commission_net)}
                            </span>
                          ) : null}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            );
          })}
        </div>

        <div className="rounded-xl border border-amber-100 bg-amber-50/50 px-3 py-2.5 space-y-1">
          <div className="flex flex-wrap justify-between gap-x-3 text-xs">
            <span className="text-gray-600">Commission kept (net)</span>
            <span className="tabular-nums font-semibold text-gray-900">
              {fullCurrency(breakdown.commission_net)}
            </span>
          </div>
          {Number(breakdown.tds_amount) > 0 ? (
            <p className="text-[11px] text-gray-500">
              Gross {fullCurrency(breakdown.commission_gross)} − TDS {fullCurrency(breakdown.tds_amount)}
            </p>
          ) : null}
          {formula.to_pay_due != null || formula.cash_to_send != null ? (
            <div className="pt-1.5 mt-1 border-t border-amber-100/80 text-[11px] text-gray-700 leading-relaxed space-y-1">
              <p className="font-semibold text-gray-800">
                Remittance on this request {fullCurrency(
                  breakdown.remittance != null ? breakdown.remittance : formula.cash_to_send,
                )}
              </p>
              {Number(formula.to_pay_gross) > 0 ? (
                <p className="tabular-nums">
                  To-Pay gross {fullCurrency(formula.to_pay_gross)}
                  {' − '}
                  commission kept {fullCurrency(formula.commission_net)}
                  {' = '}
                  <span className="font-semibold text-amber-900">
                    {fullCurrency(
                      formula.expected_remittance != null
                        ? formula.expected_remittance
                        : Math.max(0, roundMoney(formula.to_pay_gross) - roundMoney(formula.commission_net)),
                    )}
                  </span>
                  <span className="text-gray-500"> (expected net due)</span>
                </p>
              ) : null}
              <p className="tabular-nums">
                Net due in books {fullCurrency(formula.to_pay_due)}
                {' · '}
                this request {fullCurrency(
                  formula.cash_to_send != null ? formula.cash_to_send : breakdown.remittance,
                )}
              </p>
              <p className="text-[10px] text-gray-500">
                Net due is already after commission — commission is not subtracted again.
              </p>
            </div>
          ) : null}
        </div>
      </div>
    </SettlementModalShell>
  );
};

const ShipmentList = ({ shipments = [] }) => {
  if (!shipments.length) {
    return <p className="text-[11px] text-gray-500 px-2 py-1">No shipments on this ML sheet.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[11px] min-w-[520px]">
        <thead className="bg-gray-50 text-gray-600">
          <tr>
            <th className="text-left px-2 py-1.5 font-semibold">Shipment</th>
            <th className="text-left px-2 py-1.5 font-semibold">Route</th>
            <th className="text-left px-2 py-1.5 font-semibold">Payment</th>
            <th className="text-right px-2 py-1.5 font-semibold">Freight</th>
            <th className="text-right px-2 py-1.5 font-semibold">Total</th>
            <th className="text-right px-2 py-1.5 font-semibold">Balance</th>
            <th className="text-right px-2 py-1.5 font-semibold">Remitted</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {shipments.map((shipment) => (
            <tr key={shipment.shipment_id}>
              <td className="px-2 py-1.5 font-medium text-gray-900 whitespace-nowrap">
                {shipment.shipment_id}
              </td>
              <td className="px-2 py-1.5 text-gray-600 whitespace-nowrap">
                {[shipment.sender_location, shipment.receiver_location].filter(Boolean).join(' → ') || '—'}
              </td>
              <td className="px-2 py-1.5 capitalize text-gray-600 whitespace-nowrap">
                {shipment.mode_of_payment || '—'}
              </td>
              <td className="px-2 py-1.5 text-right tabular-nums">{fullCurrency(shipment.freight_charges)}</td>
              <td className="px-2 py-1.5 text-right tabular-nums">{fullCurrency(shipment.total_amount)}</td>
              <td className="px-2 py-1.5 text-right tabular-nums">{fullCurrency(shipment.balance_amount)}</td>
              <td className="px-2 py-1.5 text-right tabular-nums font-medium">
                {shipment.remittance_amount != null ? fullCurrency(shipment.remittance_amount) : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const csvEscape = (value) => {
  const text = String(value ?? '');
  if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
};

const exportToPayExcel = (batch, lines) => {
  const headers = [
    'ML Sheet',
    'Location',
    'Shipment ID',
    'From',
    'To',
    'Payment',
    'Status',
    'Freight',
    'Total',
    'Balance',
    'Commission %',
    'Commission',
    'Remittance',
  ];
  const rows = [];
  lines.forEach((line) => {
    const shipments = line.shipments || [];
    if (!shipments.length) {
      rows.push([
        line.ref,
        line.location_name || '',
        '',
        '',
        '',
        '',
        '',
        line.total_freight ?? '',
        line.amount_due ?? '',
        '',
        line.commission_pct ?? '',
        commissionOnFreight(line),
        line.amount ?? '',
      ]);
      return;
    }
    shipments.forEach((shipment, index) => {
      rows.push([
        index === 0 ? line.ref : '',
        index === 0 ? (line.location_name || '') : '',
        shipment.shipment_id || '',
        shipment.sender_location || '',
        shipment.receiver_location || '',
        shipment.mode_of_payment || '',
        shipment.status || '',
        shipment.freight_charges ?? '',
        shipment.total_amount ?? '',
        shipment.balance_amount ?? '',
        index === 0 ? (line.commission_pct ?? '') : '',
        index === 0 ? commissionOnFreight(line) : '',
        shipment.remittance_amount ?? (index === 0 ? line.amount : ''),
      ]);
    });
  });
  const csv = [headers, ...rows].map((row) => row.map(csvEscape).join(',')).join('\n');
  const name = `to_pay_remittance_${batch?.batch_id || 'settlement'}.csv`;
  downloadBlob(`\ufeff${csv}`, name, 'text/csv;charset=utf-8');
};

const ToPayLineCards = ({ rows, expandedIds, onToggle }) => (
  <div className="md:hidden space-y-2">
    {rows.map((row) => {
      const open = expandedIds.has(row.ref);
      return (
        <div key={row.key} className="rounded-xl border border-gray-100 bg-white p-3">
          <button
            type="button"
            onClick={() => onToggle(row.ref)}
            className="w-full flex items-start justify-between gap-2 text-left"
          >
            <div className="min-w-0 flex items-start gap-1.5">
              {open ? (
                <ChevronDown className="w-4 h-4 mt-0.5 text-gray-500 shrink-0" />
              ) : (
                <ChevronRight className="w-4 h-4 mt-0.5 text-gray-500 shrink-0" />
              )}
              <div className="min-w-0">
                <p className="font-semibold text-sm break-all">{row.ref}</p>
                <p className="text-[11px] text-gray-500 mt-0.5">
                  {row.location_name || '—'} · {(row.shipments || row.shipment_ids || []).length || 0} GC(s)
                </p>
              </div>
            </div>
            <p className="tabular-nums font-semibold text-sm shrink-0">
              {Number(row.amount) > 0.009
                ? fullCurrency(row.amount)
                : (row.covered_zero || row.commission_cleared ? '₹0 (audit)' : fullCurrency(0))}
            </p>
          </button>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1 mt-2 text-[11px] text-gray-600">
            <span>Net due <span className="tabular-nums font-medium text-gray-900">{fullCurrency(row.amount_due)}</span></span>
            <span>Total freight <span className="tabular-nums font-medium text-gray-900">{fullCurrency(row.total_freight)}</span></span>
            <span>Commission <span className="tabular-nums font-medium text-gray-900">{commissionLabel(row)}</span></span>
            <span>
              Remittance{' '}
              <span className="tabular-nums font-medium text-gray-900">
                {Number(row.amount) > 0.009
                  ? fullCurrency(row.amount)
                  : (row.covered_zero || row.commission_cleared ? '₹0 (in audit)' : fullCurrency(0))}
              </span>
            </span>
          </div>
          {open ? (
            <div className="mt-2 rounded-lg border border-gray-100 overflow-hidden">
              <ShipmentList shipments={row.shipments || []} />
            </div>
          ) : null}
        </div>
      );
    })}
  </div>
);

const ToPayLineTable = ({ rows, expandedIds, onToggle }) => (
  <div className="hidden md:block bg-white rounded-xl border border-gray-100 overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full text-xs min-w-[720px]">
        <thead className="bg-gray-50 text-gray-600">
          <tr>
            <th className="px-2.5 py-2 font-semibold text-left w-8" />
            <th className="px-2.5 py-2 font-semibold text-left">ML Sheet</th>
            <th className="px-2.5 py-2 font-semibold text-left">Location</th>
            <th className="px-2.5 py-2 font-semibold text-left">GCs</th>
            <th className="px-2.5 py-2 font-semibold text-right">Net due</th>
            <th className="px-2.5 py-2 font-semibold text-right">Total freight</th>
            <th className="px-2.5 py-2 font-semibold text-right">Commission</th>
            <th className="px-2.5 py-2 font-semibold text-right">Remittance</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row) => {
            const open = expandedIds.has(row.ref);
            const gcCount = (row.shipments || []).length || row.gc_count || (row.shipment_ids || []).length || 0;
            const remitLabel = Number(row.amount) > 0.009
              ? fullCurrency(row.amount)
              : (row.covered_zero || row.commission_cleared ? '₹0 (audit)' : fullCurrency(0));
            return (
              <Fragment key={row.key}>
                <tr className="hover:bg-sky-50/40">
                  <td className="px-2.5 py-2">
                    <button
                      type="button"
                      onClick={() => onToggle(row.ref)}
                      className="inline-flex items-center justify-center w-7 h-7 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
                      aria-label={open ? 'Collapse shipments' : 'Expand shipments'}
                    >
                      {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </button>
                  </td>
                  <td className="px-2.5 py-2 font-medium whitespace-nowrap">{row.ref}</td>
                  <td className="px-2.5 py-2 whitespace-nowrap">{row.location_name || '—'}</td>
                  <td className="px-2.5 py-2 text-gray-600 max-w-[200px] truncate">
                    {gcCount} · {gcLabel(row.shipment_ids || (row.shipments || []).map((s) => s.shipment_id))}
                  </td>
                  <td className="px-2.5 py-2 text-right tabular-nums whitespace-nowrap">{fullCurrency(row.amount_due)}</td>
                  <td className="px-2.5 py-2 text-right tabular-nums whitespace-nowrap">{fullCurrency(row.total_freight)}</td>
                  <td className="px-2.5 py-2 text-right tabular-nums whitespace-nowrap">{commissionLabel(row)}</td>
                  <td className="px-2.5 py-2 text-right tabular-nums whitespace-nowrap font-semibold">{remitLabel}</td>
                </tr>
                {open ? (
                  <tr className="bg-slate-50/80">
                    <td colSpan={8} className="px-3 py-2">
                      <ShipmentList shipments={row.shipments || []} />
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
);

const pickDue = (value, fallback) => (value != null && value !== '' ? value : fallback);

const mergeToPayLine = (line, dueHints) => {
  const collapse = (value) => String(value || '').replace(/\s+/g, '');
  const due = (dueHints || []).find((item) => (
    item.ml_sheet_id === line.ref || collapse(item.ml_sheet_id) === collapse(line.ref)
  )) || {};
  return {
    ...line,
    created_at: pickDue(line.created_at, due.created_at),
    location_name: pickDue(line.location_name, due.location_name),
    total_freight: pickDue(line.total_freight, due.total_freight),
    commission_pct: pickDue(line.commission_pct, due.commission_pct),
    commission_amount: pickDue(line.commission_amount, due.commission_amount),
    amount_due: pickDue(line.amount_due, due.amount_due),
    amount_returned: pickDue(line.amount_returned, due.amount_returned),
    shipment_ids: (line.shipment_ids && line.shipment_ids.length) ? line.shipment_ids : (due.shipment_ids || []),
    gc_count: line.gc_count || due.shipment_count || (due.shipment_ids || []).length,
  };
};

export default function CompanySettlementBatchView({
  batch,
  onClose,
  dueHints = [],
  canReview = false,
  busy = false,
  onApprove,
  onReject,
}) {
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [calcOpen, setCalcOpen] = useState(false);
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  if (!batch) return null;

  const primary = (batch.paid_requests || []).concat(batch.to_pay_requests || [])[0] || {};
  const ocr = primary.ocr_result_json || {};
  const checks = (ocr.checks || []).filter((c) => c.name !== 'extracted_payment_fields');
  const failedChecks = checks.filter((c) => !c.passed);
  const passedChecks = checks.filter((c) => c.passed);
  const receiptUrl = batch.receipt_url || primary.receipt_url;
  const excelUrl = primary.shipments_excel_url;
  const paidLines = batch.paid_lines || [];
  const toPayLines = (batch.to_pay_lines || []).map((line) => mergeToPayLine(line, dueHints));
  const showPaid = paidLines.length > 0 || Number(batch.paid_amount || 0) > 0;
  const showToPay = toPayLines.length > 0 || Number(batch.to_pay_amount || 0) > 0;
  const totalDue = roundMoney(toPayLines.reduce(
    (sum, line) => sum + Number(line.amount_due || 0),
    0,
  ));
  const totalFreight = roundMoney(toPayLines.reduce(
    (sum, line) => sum + Number(line.total_freight || 0),
    0,
  ));
  const breakdown = batch.commission_breakdown || null;
  const commissionAmount = breakdown?.commission_net != null
    ? roundMoney(breakdown.commission_net)
    : roundMoney(
      Number(batch.commission_net ?? batch.commission_amount)
      || toPayLines.reduce((sum, line) => sum + commissionOnFreight(line), 0),
    );
  const commissionPct = breakdown?.commission_pct
    ?? batch.commission_pct
    ?? toPayLines.find((line) => Number(line.commission_pct || 0) > 0)?.commission_pct;
  const totalRemittance = roundMoney(
    Number(batch.to_pay_amount || 0) + Number(batch.paid_amount || 0),
  );
  const toggleExpanded = (ref) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(ref)) next.delete(ref);
      else next.add(ref);
      return next;
    });
  };
  const requestForAudit = {
    ...primary,
    submitted_at: batch.submitted_at || primary.submitted_at,
    status: batch.status || primary.status,
    reviewed_by_display: batch.approved_by_display || primary.reviewed_by_display,
    reviewed_by_name: batch.approved_by_name || primary.reviewed_by_name,
    reviewed_at: batch.approved_at || primary.reviewed_at,
  };

  return (
    <>
      <SettlementModalShell
        title="Settlement request"
        subtitle={batch.batch_id}
        onClose={onClose}
        size="xl"
        headerActions={canReview ? (
          <div className="flex items-center gap-1.5">
            <Button
              variant="secondary"
              size="sm"
              icon={X}
              mobileIconOnly
              disabled={busy}
              onClick={() => onReject?.(batch.batch_id)}
            >
              Reject
            </Button>
            <Button
              variant="primary"
              size="sm"
              icon={Check}
              mobileIconOnly
              disabled={busy}
              onClick={() => onApprove?.(batch.batch_id)}
            >
              Approve
            </Button>
          </div>
        ) : null}
      >
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-start text-sm">
            <div className="min-w-0 flex flex-col gap-2">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <AmountCard
                    label="Total"
                    value={totalDue > 0 ? totalDue : batch.total_amount}
                    tone="emerald"
                  />
                  {showToPay ? (
                    <AmountCard label="Total freight" value={totalFreight} tone="sky" />
                  ) : null}
                  <AmountCard
                    label={
                      commissionPct != null
                        ? (breakdown ? `Commission kept (${commissionPct}%)` : `Commission (${commissionPct}%)`)
                        : (breakdown ? 'Commission kept' : 'Commission')
                    }
                    value={commissionAmount}
                    tone="violet"
                  />
                  <AmountCard label="Total remittance" value={totalRemittance} tone="amber" />
                  {showPaid && !showToPay ? (
                    <AmountCard label="Paid" value={batch.paid_amount} tone="amber" />
                  ) : null}
                </div>
                {showToPay && !breakdown ? (
                  <p className="text-[11px] text-gray-600 leading-snug">
                    Remittance is the cash you sent after commission on all open To-Pay sheets,
                    then applied to this ML (not always Total − this sheet&apos;s commission).
                  </p>
                ) : null}

                {breakdown ? (
                  <CommissionCalcSummaryCard
                    breakdown={breakdown}
                    onOpen={() => setCalcOpen(true)}
                  />
                ) : null}

                <SettlementFieldList
                  items={[
                    {
                      label: 'Status',
                      value: (
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs border ${statusBadge(batch.status)}`}>
                          {statusLabel(batch.status)}
                        </span>
                      ),
                    },
                    {
                      label: 'AI review',
                      value: aiReviewLabel(primary.ocr_status, batch.status),
                    },
                    {
                      label: 'Mode',
                      value: `${batch.payment_mode || '—'}${batch.payment_ref ? ` · ${batch.payment_ref}` : ''}`,
                      span: 2,
                    },
                  ]}
                />

                <SettlementAuditFields request={requestForAudit} />

                {(primary.ocr_notes || primary.review_notes || batch.remarks) && (
                  <div className="rounded-xl border border-sky-100 bg-sky-50/60 px-2.5 py-1.5">
                    <div className="text-xs font-semibold text-sky-900 mb-1">Summary</div>
                    <p className="text-xs text-gray-700 whitespace-pre-wrap break-words">
                      {friendlyNotes(primary.ocr_notes || primary.review_notes || batch.remarks)}
                    </p>
                  </div>
                )}

                {receiptUrl ? (
                  <SettlementReceiptThumb url={receiptUrl} onView={() => setReceiptOpen(true)} showDownload />
                ) : null}

              {excelUrl ? (
                <div className="flex flex-wrap gap-2 pt-0.5">
                  <a
                    href={excelUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-xl border border-sky-200 bg-sky-50 text-sky-800 hover:bg-sky-100"
                  >
                    <Download className="w-3.5 h-3.5" />
                    Download Excel
                  </a>
                </div>
              ) : null}
            </div>

            <div className="min-w-0 flex flex-col rounded-xl border border-gray-200 border-l-4 border-l-orange-400 bg-white p-3 space-y-2">
                <div className="text-xs font-semibold text-gray-800">
                  AI review — {aiReviewLabel(primary.ocr_status, batch.status)}
                </div>
                  {failedChecks.length > 0 && (
                    <div>
                      <div className="text-[11px] font-semibold text-red-800 mb-1">
                        Failed checks ({failedChecks.length})
                      </div>
                      <ul className="space-y-1">
                        {failedChecks.map((check) => (
                          <li key={check.name} className="text-[11px] text-red-800 flex gap-1.5">
                            <span className="shrink-0 font-semibold">FAIL</span>
                            <span className="break-words">{friendlyNotes(check.detail)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {passedChecks.length > 0 && (
                    <div>
                      <div className="text-[11px] font-semibold text-green-800 mb-1">
                        Passed checks ({passedChecks.length})
                      </div>
                      <ul className="space-y-1">
                        {passedChecks.map((check) => (
                          <li key={check.name} className="text-[11px] text-green-700 flex gap-1.5">
                            <span className="shrink-0 font-semibold">PASS</span>
                            <span className="break-words">{friendlyNotes(check.detail)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <ReceiptExtractedFields ocrResult={ocr} />
                  {checks.length === 0 && (
                    <p className="text-xs text-orange-800">No AI checks yet.</p>
                  )}
            </div>
          </div>

          <div className="flex flex-col gap-3">
          {paidLines.length > 0 ? (
            <>
              <PaidLineCards rows={paidLines.map((line) => ({ ...line, key: `p-${line.ref}` }))} />
              <LineTable
              title="Paid remittance"
              rows={paidLines.map((line) => ({ ...line, key: `p-${line.ref}` }))}
              columns={[
                { key: 'ref', label: 'GC', render: (row) => row.ref },
                { key: 'date', label: 'Date', render: (row) => formatIndianDate(row.booking_date) },
                {
                  key: 'paid',
                  label: 'Paid',
                  align: 'right',
                  render: (row) => fullCurrency(row.paid_amount),
                },
                {
                  key: 'prev',
                  label: 'Already remitted',
                  align: 'right',
                  render: (row) => fullCurrency(row.previously_remitted),
                },
                {
                  key: 'amount',
                  label: 'This remittance',
                  align: 'right',
                  render: (row) => fullCurrency(row.amount),
                },
                {
                  key: 'remain',
                  label: 'Remaining',
                  align: 'right',
                  render: (row) => fullCurrency(row.remaining_after),
                },
                { key: 'mode', label: 'Mode', render: (row) => row.mode_of_payment || '—' },
                {
                  key: 'status',
                  label: 'Status',
                  render: (row) => row.paid_remit_status || '—',
                  className: 'capitalize',
                },
              ]}
            />
            </>
          ) : null}

          {toPayLines.length > 0 ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-xs font-semibold text-gray-800">To-Pay remittance</h3>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => exportToPayExcel(batch, toPayLines)}
                  className="!px-2.5 !py-1.5 text-xs"
                >
                  <FileSpreadsheet className="w-3.5 h-3.5 mr-1" />
                  Excel
                </Button>
              </div>
              <ToPayLineCards
                rows={toPayLines.map((line) => ({ ...line, key: `t-${line.ref}` }))}
                expandedIds={expandedIds}
                onToggle={toggleExpanded}
              />
              <ToPayLineTable
                rows={toPayLines.map((line) => ({ ...line, key: `t-${line.ref}` }))}
                expandedIds={expandedIds}
                onToggle={toggleExpanded}
              />
            </div>
          ) : null}
          </div>
        </div>
      </SettlementModalShell>

      {receiptOpen && receiptUrl ? (
        <DocumentModal
          isOpen={receiptOpen}
          onClose={() => setReceiptOpen(false)}
          documentUrl={receiptUrl}
          documentType="image"
          title="Payment receipt"
          zIndexClass="z-[90]"
        />
      ) : null}

      {calcOpen && breakdown ? (
        <CommissionBreakdownModal
          breakdown={breakdown}
          batchId={batch.batch_id}
          onClose={() => setCalcOpen(false)}
        />
      ) : null}
    </>
  );
}
